import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Internal Sales Team members a reservation can be credited to
// (Front Office > Reservations, Booking Source = Sales Team).
const emptyForm = {
  member_name: '',
  employee_code: '',
  mobile_number: '',
  email: '',
  is_active: true
};

function PmsSalesTeamMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    try {
      const res = await fetch('/api/v1/pms/sales-team?all=1', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || `Failed to load team members (HTTP ${res.status})`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load team members');
    }
  };

  useEffect(() => {
    setError(''); setMessage(''); setEditingId(''); setForm(emptyForm);
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const resetForm = () => { setEditingId(''); setForm(emptyForm); };

  const onSave = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.member_name || '').trim()) return setError('Member Name is required');
    const mobile = String(form.mobile_number || '').trim();
    if (mobile && !/^\d{10}$/.test(mobile)) return setError('Mobile number must be exactly 10 digits');
    const email = String(form.email || '').trim();
    if (email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(email)) return setError('Enter a valid email address');
    setError(''); setMessage('');
    try {
      const payload = {
        member_name: String(form.member_name || '').trim(),
        employee_code: String(form.employee_code || '').trim(),
        mobile_number: mobile,
        email,
        is_active: form.is_active !== false
      };
      const url = editingId ? `/api/v1/pms/sales-team/${editingId}` : '/api/v1/pms/sales-team';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save team member');
      setMessage(`Team member ${editingId ? 'updated' : 'added'} successfully`);
      resetForm();
      loadRows();
    } catch (err) {
      setError(err?.message || 'Failed to save team member');
    }
  };

  const onEdit = (row) => {
    setEditingId(String(row.member_id));
    setForm({
      member_name: row.member_name || '',
      employee_code: row.employee_code || '',
      mobile_number: row.mobile_number || '',
      email: row.email || '',
      is_active: row.is_active !== false
    });
    setMessage(''); setError('');
    scrollToForm();
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete ${row.member_name} from the Sales Team?`)) return;
    setError(''); setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/sales-team/${row.member_id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to delete team member');
      setMessage(data.deactivated
        ? `${row.member_name} is linked to existing reservations, so they were marked Inactive instead of deleted.`
        : `${row.member_name} deleted`);
      if (String(editingId) === String(row.member_id)) resetForm();
      loadRows();
    } catch (err) {
      setError(err?.message || 'Failed to delete team member');
    }
  };

  const th = { position: 'sticky', top: 0, zIndex: 2, background: 'var(--theme-primary)', color: '#fff', padding: '0.45rem', textAlign: 'left' };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Sales Team</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div ref={formRef} className="filter-bar" style={{ marginBottom: '0.8rem', ...editHighlightStyle(editHighlight) }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: '0.5rem' }}>
          <input placeholder="Member Name *" maxLength={120} value={form.member_name} onChange={(e) => setForm({ ...form, member_name: e.target.value })} />
          <input placeholder="Employee Code (optional)" maxLength={40} value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} />
          <input type="tel" inputMode="numeric" maxLength={10} placeholder="Mobile (10 digits)" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select value={form.is_active ? 'Y' : 'N'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'Y' })}>
            <option value="Y">Active</option>
            <option value="N">Inactive</option>
          </select>
        </div>
        <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={onSave}>{editingId ? 'Update Member' : 'Add Member'}</button>
          <button onClick={resetForm}>Clear</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr>
              {['Member Name', 'Employee Code', 'Mobile', 'Email', 'Status', 'Action'].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.member_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ padding: '0.45rem', fontWeight: 600 }}>{r.member_name}</td>
                <td style={{ padding: '0.45rem' }}>{r.employee_code || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.mobile_number || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.email || '-'}</td>
                <td style={{ padding: '0.45rem', color: r.is_active ? '#276749' : '#a0aec0', fontWeight: 600 }}>{r.is_active ? 'ACTIVE' : 'INACTIVE'}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button onClick={() => onEdit(r)}>Edit</button>
                  <button onClick={() => onDelete(r)} style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2' }}>Delete</button>
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={6} style={{ padding: '0.8rem', textAlign: 'center', color: '#718096' }}>
                  No Sales Team members yet. Add them above to select them on reservations (Booking Source = Sales Team).
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsSalesTeamMaster;
